import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Building2, Upload, X, Save, Image, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export interface CompanyData {
  id?: string;
  name: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  email: string;
  phone: string;
  logo_url: string | null;
}

const defaultCompanyData: CompanyData = {
  name: "",
  street: "",
  houseNumber: "",
  postalCode: "",
  city: "",
  email: "",
  phone: "",
  logo_url: null,
};

export const getCompanyData = async (): Promise<CompanyData> => {
  const { data, error } = await supabase
    .from("company_settings")
    .select("*")
    .limit(1)
    .maybeSingle();

  if (error || !data) {
    return defaultCompanyData;
  }

  return {
    id: data.id,
    name: data.name || "",
    street: data.street || "",
    houseNumber: data.house_number || "",
    postalCode: data.postal_code || "",
    city: data.city || "",
    email: data.email || "",
    phone: data.phone || "",
    logo_url: data.logo_url,
  };
};

const CompanySettings = () => {
  const [companyData, setCompanyData] = useState<CompanyData>(defaultCompanyData);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadCompanyData();
  }, []);

  const loadCompanyData = async () => {
    setLoading(true);
    try {
      const data = await getCompanyData();
      setCompanyData(data);
    } catch (error) {
      console.error("Error loading company data:", error);
      toast.error("Fehler beim Laden der Firmendaten");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CompanyData, value: string) => {
    setCompanyData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Bitte nur Bilddateien hochladen");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Datei zu groß. Maximal 2MB erlaubt.");
      return;
    }

    setUploading(true);
    try {
      // Delete old logo if exists
      if (companyData.logo_url) {
        const oldPath = companyData.logo_url.split('/').pop();
        if (oldPath) {
          await supabase.storage.from("company-assets").remove([`logos/${oldPath}`]);
        }
      }

      // Upload new logo
      const fileExt = file.name.split('.').pop();
      const fileName = `company-logo-${Date.now()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("company-assets")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("company-assets")
        .getPublicUrl(filePath);

      setCompanyData(prev => ({ ...prev, logo_url: urlData.publicUrl }));
      setHasChanges(true);
      toast.success("Logo hochgeladen");
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Fehler beim Hochladen des Logos");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (companyData.logo_url) {
      try {
        const urlParts = companyData.logo_url.split('/');
        const fileName = urlParts[urlParts.length - 1];
        await supabase.storage.from("company-assets").remove([`logos/${fileName}`]);
      } catch (error) {
        console.error("Error removing logo:", error);
      }
    }
    setCompanyData(prev => ({ ...prev, logo_url: null }));
    setHasChanges(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        name: companyData.name || null,
        street: companyData.street || null,
        house_number: companyData.houseNumber || null,
        postal_code: companyData.postalCode || null,
        city: companyData.city || null,
        email: companyData.email || null,
        phone: companyData.phone || null,
        logo_url: companyData.logo_url,
      };

      if (companyData.id) {
        const { error } = await supabase
          .from("company_settings")
          .update(updateData)
          .eq("id", companyData.id);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("company_settings")
          .insert(updateData)
          .select()
          .single();

        if (error) throw error;
        if (data) {
          setCompanyData(prev => ({ ...prev, id: data.id }));
        }
      }

      setHasChanges(false);
      window.dispatchEvent(new Event('storage-sync'));
      toast.success("Firmendaten gespeichert");
    } catch (error) {
      console.error("Error saving company data:", error);
      toast.error("Fehler beim Speichern der Firmendaten");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Firmendaten
        </CardTitle>
        <CardDescription>
          Diese Daten erscheinen auf allen PDF-Dokumenten (Logo oben links, Firmendaten im Footer)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Logo Upload */}
        <div className="space-y-2">
          <Label>Firmenlogo</Label>
          <div className="flex items-start gap-4">
            {companyData.logo_url ? (
              <div className="relative">
                <img 
                  src={companyData.logo_url} 
                  alt="Firmenlogo" 
                  className="h-24 w-auto max-w-48 object-contain border rounded-md p-2 bg-background"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute -top-2 -right-2 h-6 w-6"
                  onClick={handleRemoveLogo}
                  disabled={saving}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="h-24 w-48 border-2 border-dashed rounded-md flex items-center justify-center text-muted-foreground">
                <Image className="h-8 w-8" />
              </div>
            )}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Logo hochladen
              </Button>
              <p className="text-xs text-muted-foreground mt-1">
                PNG, JPG oder SVG. Max. 2MB.
              </p>
            </div>
          </div>
        </div>

        {/* Company Name */}
        <div className="space-y-2">
          <Label htmlFor="company-name">Firmenname</Label>
          <Input
            id="company-name"
            value={companyData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="Musterfirma GmbH"
          />
        </div>

        {/* Address */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2 space-y-2">
            <Label htmlFor="street">Straße</Label>
            <Input
              id="street"
              value={companyData.street}
              onChange={(e) => handleInputChange("street", e.target.value)}
              placeholder="Musterstraße"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="house-number">Hausnummer</Label>
            <Input
              id="house-number"
              value={companyData.houseNumber}
              onChange={(e) => handleInputChange("houseNumber", e.target.value)}
              placeholder="123"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="postal-code">Postleitzahl</Label>
            <Input
              id="postal-code"
              value={companyData.postalCode}
              onChange={(e) => handleInputChange("postalCode", e.target.value)}
              placeholder="12345"
            />
          </div>
          <div className="sm:col-span-2 space-y-2">
            <Label htmlFor="city">Ort</Label>
            <Input
              id="city"
              value={companyData.city}
              onChange={(e) => handleInputChange("city", e.target.value)}
              placeholder="Musterstadt"
            />
          </div>
        </div>

        {/* Contact */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail-Adresse</Label>
            <Input
              id="email"
              type="email"
              value={companyData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="info@musterfirma.de"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Telefonnummer</Label>
            <Input
              id="phone"
              type="tel"
              value={companyData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              placeholder="+49 123 456789"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <Button onClick={handleSave} disabled={!hasChanges || saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanySettings;
